import { useEffect, useMemo, useRef } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Crosshair, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * LocationPicker — mapa OSM con click-to-pin para seleccionar lat/long.
 *
 * Props:
 *   value      = { lat, lng } actual
 *   onChange   = callback al elegir una ubicación
 *   heightPx   = altura fija para que no se deforme con flexbox
 *
 * Usa tiles de OpenStreetMap (gratis, sin API key).
 * Default center = Ciudad de México si no hay value.
 * Botón "Mi ubicación" usa Geolocation API del navegador si el user lo permite.
 * Link a Google Maps para navegar desde el point seleccionado (útil para técnicos).
 */

// Fix para iconos default de Leaflet que se rompen con bundlers modernos (Vite/Webpack).
// Importa las imágenes y las asigna al prototype de Icon.Default.
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER: [number, number] = [19.4326, -99.1332]; // CDMX
const DEFAULT_ZOOM = 13;

type LatLng = { lat: number; lng: number };

type Props = {
  value?: LatLng | null;
  onChange: (loc: LatLng) => void;
  heightPx?: number;
  readOnly?: boolean;
  /** Capa de tiles. "street" (default, OSM), "satellite" (Esri World Imagery)
   *  o "hybrid" (rc105: satélite + overlays Esri de vías y nombres). */
  tiles?: "street" | "satellite" | "hybrid";
};

function ClickHandler({ onPick }: { onPick: (loc: LatLng) => void }) {
  useMapEvents({
    click: (e) => {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function FlyTo({ to }: { to: LatLng | null }) {
  const map = useMap();
  useEffect(() => {
    if (to) map.flyTo([to.lat, to.lng], Math.max(map.getZoom(), 15), { duration: 0.6 });
  }, [to, map]);
  return null;
}

export function LocationPicker({ value, onChange, heightPx = 260, readOnly = false, tiles = "street" }: Props) {
  const mapRef = useRef<L.Map | null>(null);
  const center = useMemo<[number, number]>(() => {
    return value ? [value.lat, value.lng] : DEFAULT_CENTER;
  }, [value]);

  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      alert("Tu navegador no soporta geolocalización.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onChange(loc);
      },
      (err) => alert(`No se pudo obtener ubicación: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  }

  function googleMapsUrl(lat: number, lng: number) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className="overflow-hidden rounded-md border"
        style={{ height: `${heightPx}px`, isolation: "isolate", position: "relative" }}
      >
        <MapContainer
          center={center}
          zoom={value ? 16 : DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%" }}
          ref={(instance) => {
            if (instance) mapRef.current = instance;
          }}
          scrollWheelZoom
        >
          {tiles === "satellite" || tiles === "hybrid" ? (
            <>
              <TileLayer
                attribution='Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              {/* rc105 — Híbrido: overlays Esri de vías + nombres de lugares */}
              {tiles === "hybrid" && (
                <>
                  <TileLayer
                    attribution=""
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                  <TileLayer
                    attribution=""
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={19}
                  />
                </>
              )}
            </>
          ) : (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}
          {!readOnly && <ClickHandler onPick={onChange} />}
          {/* rc103 — marcador ARRASTRABLE cuando no es readOnly: además del
              click-to-pin, se puede afinar la posición jalando el pin. */}
          {value && (
            <Marker
              position={[value.lat, value.lng]}
              draggable={!readOnly}
              eventHandlers={
                readOnly
                  ? undefined
                  : {
                      dragend: (e) => {
                        const ll = (e.target as L.Marker).getLatLng();
                        onChange({ lat: ll.lat, lng: ll.lng });
                      },
                    }
              }
            />
          )}
          <FlyTo to={value ?? null} />
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {value ? (
          <>
            <span className="inline-flex items-center gap-1 font-mono text-muted-foreground">
              <MapPin size={12} />
              {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
            </span>
            <a
              href={googleMapsUrl(value.lat, value.lng)}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <ExternalLink size={11} />
              Abrir en Google Maps
            </a>
          </>
        ) : (
          <span className="text-muted-foreground">
            {readOnly ? "Sin coordenadas" : "Haz click en el mapa para seleccionar la ubicación"}
          </span>
        )}
        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleUseMyLocation}
            className="ml-auto"
          >
            <Crosshair size={12} /> Mi ubicación
          </Button>
        )}
      </div>
    </div>
  );
}
